import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * DÜZELTME: Bu bileşenin kademe listesi (TIERS) Shopify Admin'deki
 * gerçek otomatik indirimlerle (500/750/1000/1500/2000/2500 TL) elle
 * senkron tutulmalı. Kademeler admin'de değişirse burası da güncellenmeli.
 * 2500 TL "Keefla Exclusive" kademesi — parıltı (ulaşılmadan önce) /
 * alev (ulaşılınca) efekti alan özel kademe.
 */
const TIERS = [
  { min: 0, pct: 0 },
  { min: 500, pct: 5 },
  { min: 750, pct: 7 },
  { min: 1000, pct: 10 },
  { min: 1500, pct: 13 },
  { min: 2000, pct: 19 },
  { min: 2500, pct: 25 },
];

const FREE_SHIPPING_MIN = 1000;

const FLAME_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2c-1 4-6 5-6 11a6 6 0 0 0 12 0c0-3-1.5-4.5-2-6 0 2-1.5 3.5-3 3.5A2.5 2.5 0 0 1 9.5 8c0-2 1.5-3 2.5-6z"/></svg>';

/**
 * Sepet toplamına göre "bir sonraki kademeye X TL kaldı" mesajı ve
 * renkli kademe rozetleri gösteren, sayfanın altına sabitlenmiş bar.
 * Sepet her değiştiğinde (ekle/çıkar/miktar güncelle)
 * StandardEvents.cartLinesUpdate olayını dinleyip /cart.js'den güncel
 * toplamı çeker.
 *
 * DÜZELTME (ACİL, ilk sürüm): Sepet çekmecesi bir <dialog> olarak
 * showModal() ile açılıyor ve bu bar position:fixed ile ekranın altını
 * kapladığı için çekmecenin "Ödemeye geç" butonunun üzerine biniyordu.
 *
 * DÜZELTME 2: Tamamen gizlemek yerine, açık olan dialog tam sayfa
 * genişliğini kaplamıyorsa (masaüstünde sepet çekmecesi gibi sağda sabit
 * genişlikli bir panelse) bar sadece o panelin solundaki boş alana
 * sığacak şekilde daralıyor ("width" ile — "right" temanin RTL mantıksal
 * inset-inline-end kuralıyla çakışıp işe yaramıyordu).
 *
 * DÜZELTME 3: Marka renkleri (terracotta, klasik mavi, bordo, mint,
 * kurumsal kırmızı) kullanan kademe rozetleri, 1000 TL'de sabit
 * kargo-bedava çizgisi, ve son kademe (2500 TL) için özel "Keefla
 * Exclusive" durumu: ulaşılmadan önce altın parıltı, ulaşılınca sürekli
 * yanan alev efekti.
 *
 * DÜZELTME 4 (KRİTİK — "sayfa yenilenince yansıyor" hatası): cartLinesUpdate
 * olayı, sepet mutasyonu SUNUCUDA TAMAMLANMADAN ÖNCE (istek atılırken)
 * fırlatılıyor — asıl güncel veri ancak olayın taşıdığı `event.promise`
 * çözüldüğünde hazır oluyor (bkz. product-form.js / cart-icon.js, aynı
 * deseni kullanıyorlar). Eskiden bu promise'i beklemeden direkt /cart.js
 * çekiyorduk; bu da sunucu isteği bitmeden önceki ESKİ veriyi almamıza
 * yol açıyordu (görünürde "güncellenmiyor", aslında yarış durumu/race
 * condition). Artık event.promise'in çözülmesini bekleyip ONDAN SONRA
 * /cart.js çekiyoruz — cart-icon.js'in sepet ikonu güncellemesiyle
 * birebir aynı, kanıtlanmış desen.
 *
 * DÜZELTME 5 ("boş sepette de görünsün"): Bar artık sepet boşken de
 * gösteriliyor ("500 TL daha ekle, %5 indirim kazan" gibi bir mesajla
 * ilk alışverişi teşvik ediyor) — eskiden itemCount === 0 durumunda
 * tamamen gizleniyordu.
 *
 * @extends {Component}
 */
class TierProgressBar extends Component {
  requiredRefs = ['message', 'fill', 'chips', 'shipLine', 'shipLabel'];

  /** @type {MutationObserver | undefined} */
  #dialogObserver;

  /** @type {number} */
  #itemCount = 0;

  /** @type {number} */
  #subtotal = 0;

  /** @type {number} */
  #resizeRaf = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.refresh);
    window.addEventListener('resize', this.#handleResize);

    this.#dialogObserver = new MutationObserver(this.#syncDialogState);
    this.#dialogObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['open'],
      subtree: true,
    });

    this.#positionShippingMarker();
    this.#syncDialogState();
    this.refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.refresh);
    window.removeEventListener('resize', this.#handleResize);
    this.#dialogObserver?.disconnect();
  }

  /**
   * cartLinesUpdate olayı sunucu isteği TAMAMLANMADAN önce fırlatılır;
   * event.promise, sunucu yanıtı (ve ardından tazelenen sepet verisi)
   * hazır olduğunda çözülür. O yüzden /cart.js'i hemen değil, promise
   * çözüldükten SONRA çekiyoruz — aksi halde sepet mutasyonu sunucuda
   * bitmeden eski veriyi alıp bar'ı güncellememiş oluyorduk.
   *
   * @param {CustomEvent & { promise?: Promise<unknown> }} event
   */
  #onCartUpdate = (event) => {
    if (!event.promise) {
      this.refresh();
      return;
    }
    event.promise
      .then(() => this.refresh())
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[tier-progress-bar] event promise rejected:', error);
      });
  };

  #handleResize = () => {
    if (this.#resizeRaf) cancelAnimationFrame(this.#resizeRaf);
    this.#resizeRaf = requestAnimationFrame(() => {
      if (document.querySelector('dialog[open]')) this.#syncDialogState();
    });
  };

  /**
   * Kargo bedava çizgisini (1000 TL), en üst kademeye (2500 TL) oranla
   * sabit bir konuma yerleştirir. Kademeler değişirse otomatik yeniden
   * hesaplanır — CSS içinde sabit yüzde olarak tutulmuyor.
   */
  #positionShippingMarker() {
    const topTierMin = TIERS[TIERS.length - 1].min;
    const pct = Math.min(100, (FREE_SHIPPING_MIN / topTierMin) * 100);
    this.refs.shipLine.style.left = `${pct}%`;
    this.refs.shipLabel.style.left = `${pct}%`;
  }

  /**
   * Açık bir <dialog> varsa (sepet çekmecesi, arama, hızlı ekle, vb.)
   * genişliğini ölçer. Sayfanın tamamını kaplamıyorsa bar daralıp o
   * dialogun soluna sığar; tamamını kaplıyorsa (mobilde tam ekran vb.)
   * sığacak yer kalmadığından bar tamamen gizlenir.
   */
  #syncDialogState = () => {
    const openDialog = document.querySelector('dialog[open]');

    if (!openDialog) {
      this.classList.remove('tier-progress-bar--dialog-open', 'tier-progress-bar--narrowed');
      this.style.width = '';
      return;
    }

    const rect = openDialog.getBoundingClientRect();
    const coversFullWidth = rect.left <= 1 && rect.width >= window.innerWidth - 1;

    if (coversFullWidth) {
      this.classList.add('tier-progress-bar--dialog-open');
      this.classList.remove('tier-progress-bar--narrowed');
      this.style.width = '';
    } else {
      this.classList.remove('tier-progress-bar--dialog-open');
      this.classList.add('tier-progress-bar--narrowed');
      this.style.width = `${Math.max(0, rect.left)}px`;
    }
  };

  refresh = async () => {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`.replace('//', '/'));
      if (!response.ok) return;
      const cart = await response.json();
      this.#itemCount = cart.item_count ?? 0;
      this.#subtotal = (cart.total_price ?? 0) / 100;
      this.render(this.#itemCount, this.#subtotal);
    } catch (error) {
      console.warn('[tier-progress-bar] sepet bilgisi alınamadı:', error);
    }
  };

  /**
   * @param {number} itemCount
   * @param {number} subtotal
   */
  render(itemCount, subtotal) {
    // DÜZELTME: Sepet boşken de gösteriliyor artık — subtotal 0 olunca
    // TIERS[0] (min:0) devreye girer ve "500 TL daha ekle, %5 indirim
    // kazan" gibi bir mesajla ilk alışverişi teşvik eder. Eskiden
    // itemCount === 0 durumunda bar tamamen gizleniyordu.
    this.classList.remove('tier-progress-bar--hidden');
    this.setAttribute('aria-hidden', 'false');

    const reversedTiers = [...TIERS].reverse();
    const current = reversedTiers.find((tier) => subtotal >= tier.min) ?? TIERS[0];
    const next = TIERS.find((tier) => tier.min > subtotal) ?? null;

    this.refs.message.textContent = next
      ? `${Math.ceil(next.min - subtotal)} TL daha ekle, %${next.pct} indirim kazan`
      : `En üst kademedesin, %${current.pct} indirim kazandın`;

    const topTierMin = TIERS[TIERS.length - 1].min;
    const fillPct = Math.min(100, (subtotal / topTierMin) * 100);
    this.refs.fill.style.width = `${fillPct}%`;

    this.refs.chips.querySelectorAll('[data-min]').forEach((chip) => {
      const min = Number(chip.getAttribute('data-min'));
      const reached = subtotal >= min;
      chip.classList.toggle('is-reached', reached);

      if (chip.classList.contains('tier-progress-bar__chip--excl')) {
        this.#syncExclusiveChip(chip, reached);
      }
    });
  }

  /**
   * "Keefla Exclusive" (2500 TL) rozetinin alev ikonlarını günceller.
   * Ulaşılmadan önce hiçbir ikon eklenmez — altın kenarlık nabzı + gökkuşağı
   * parıltısı (shimmer, bkz. CSS) tek başına yeterli, onaylanan tasarımda
   * (kademe_cubugu_v9) ayrıca bir "parıldama ikonu" yok. Aynı duruma tekrar
   * tekrar ikon eklememek için mevcut durumu data-state ile takip eder.
   *
   * @param {HTMLElement} chip
   * @param {boolean} reached
   */
  #syncExclusiveChip(chip, reached) {
    if (chip.dataset.state === String(reached)) return;
    chip.dataset.state = String(reached);

    chip.querySelectorAll('.tier-progress-bar__flame').forEach((node) => node.remove());
    if (!reached) return;

    for (let i = 0; i < 3; i++) {
      const wrapper = document.createElement('span');
      wrapper.className = 'tier-progress-bar__flame';
      wrapper.innerHTML = FLAME_SVG;
      chip.appendChild(wrapper);
    }
  }
}

if (!customElements.get('tier-progress-bar')) {
  customElements.define('tier-progress-bar', TierProgressBar);
}
